import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/utils/cn';

export function MarkdownText({ content, className }: { content: string, className?: string }) {
  // Loại bỏ các thẻ HTML thừa (như <br>) do AI đôi khi sinh ra sai chuẩn Markdown
  const cleanContent = content.replace(/<br\s*\/?>/gi, '\n');

  return (
    <div className={cn("markdown-container max-w-none text-sm text-slate-700 leading-relaxed", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-3 whitespace-pre-line last:mb-0" {...props} />,
          h1: ({ node, ...props }) => <h1 className="text-xl font-medium mt-6 mb-3 text-emerald-800" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-lg font-medium mt-5 mb-2 text-emerald-700" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-base font-medium mt-4 mb-2 text-emerald-700 tracking-widest tracking-wide" {...props} />,
          h4: ({ node, ...props }) => <h4 className="text-sm font-medium mt-3 mb-1 text-slate-800" {...props} />,
          ul: ({ node, ...props }) => <ul className="my-3 space-y-2 list-disc list-outside ml-5 text-slate-700 marker:text-emerald-500" {...props} />,
          ol: ({ node, ...props }) => <ol className="my-3 space-y-2 list-decimal list-outside ml-5 text-slate-700 marker:text-emerald-600 font-medium" {...props} />,
          li: ({ node, ...props }) => <li className="pl-1" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-medium text-slate-900" {...props} />,
          em: ({ node, ...props }) => <em className="italic text-slate-600" {...props} />,
          hr: ({ node, ...props }) => <hr className="my-5 border-emerald-100" {...props} />,
          
          // Table parsing support via remark-gfm
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-5 rounded-xl border border-slate-200 shadow-sm">
              <table className="w-full text-left border-collapse text-sm m-0" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200" {...props} />,
          tbody: ({ node, ...props }) => <tbody className="divide-y divide-slate-100 bg-white" {...props} />,
          tr: ({ node, ...props }) => <tr className="hover:bg-slate-50/50 transition-colors" {...props} />,
          th: ({ node, ...props }) => <th className="px-4 py-3 align-middle font-medium text-emerald-800 bg-emerald-50/50" {...props} />,
          td: ({ node, ...props }) => <td className="px-4 py-3 align-top" {...props} />,
          
          a: ({ node, ...props }) => <a className="text-emerald-600 hover:text-emerald-700 underline font-medium" target="_blank" rel="noopener noreferrer" {...props} />,
          
          code: ({ node, className, children, ...props }: any) => {
            const isBlock = /language-(\w+)/.exec(className || '') || String(children).includes('\n');
            if (isBlock) {
              return <code className={cn("text-slate-700 font-mono text-[13px] font-medium leading-relaxed", className)} {...props}>{children}</code>;
            }
            return <code className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 border border-emerald-200/60 rounded text-[13px] font-mono font-medium" {...props}>{children}</code>;
          },
          pre: ({ node, ...props }) => (
            <pre className="bg-emerald-50/50 text-slate-800 p-4 rounded-xl overflow-x-auto my-4 shadow-sm whitespace-pre-wrap break-words leading-relaxed border border-emerald-100/80" {...props} />
          )
        }}
      >
        {cleanContent}
      </ReactMarkdown>
    </div>
  );
}
